/* eslint-disable no-console */
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Card, Button, Loader, Group } from '@mantine/core';
import { AppContext } from '../../contexts';
import parse from 'html-react-parser';
import dompurify from 'dompurify';
import { getTermConditionById } from '../../utils/api';
import styles from './styles.scoped.css';
import BreadcrumbLink from '../../components/elements/BreadcrumbLink/BreadcrumbLink';
import { typeTab } from './constants';

export default function TermConditionPreview() {
  const { setBreadcrumbs } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const agreementId = searchParams.get('agreementId');
  const historyId = searchParams.get('historyId');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (!historyId) {
          return;
        }

        const response = await getTermConditionById(historyId);
        const responseData = response.data?.data || response.data;

        if (responseData) {
          setData(responseData);
          const tabName = typeTab[type] || 'Pangkalan';

          const handleTabClick = () => {
            // TBD: adjust the search param based on the type
            const searchParam ='?tab=subpangkalan';
            // const searchParam = isPangkalan ? '' : '?tab=subpangkalan';
            navigate(`/term-condition${searchParam}`);
          };

          setBreadcrumbs([
            'Manajemen Kebijakan',
            <BreadcrumbLink key="tab-link" onClick={handleTabClick} text={tabName} />,
            `Detail Kebijakan`
          ]);
        }
      } catch (error) {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    if (historyId) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [historyId, setBreadcrumbs, history]);

  const handleBack = () => {
    // TBD: adjust the search param based on the type
    const searchParam ='?tab=subpangkalan';
    // const searchParam = isPangkalan ? '' : '?tab=subpangkalan';
    navigate(`/term-condition${searchParam}`);
  };

  const handleUpdate = () => {
    if (agreementId && historyId) {
      navigate({
        pathname: `/term-condition/update`,
        search: `?agreementId=${agreementId}&historyId=${historyId}`,
        state: {
          initialData: data,
          userType: type
        }
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (isActive) => {
    return isActive ? '#0F6EB5' : '#CF212A';
  };

  const getRequiredColor = (isRequired) => {
    return isRequired ? '#0F6EB5' : '#FF711A';
  };

  if (isLoading) {
    return (
      <div className={styles.previewLoading}>
        <Loader size={40} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.previewError}>
        <p>Data tidak ditemukan</p>
        <Button onClick={handleBack}>Kembali</Button>
      </div>
    );
  }

  return (
    <div className={styles.previewContainer}>
      <Card className={styles.previewCard}>
        <div className={styles.versionInfo}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px 4px 0',
          }}>
            <span style={{ fontWeight: 400 }}>Versi:</span>
            <span style={{ marginLeft: '4px' }}>{data.version || '-'}</span>
          </div>

          {data.createdAt && (
            <>
              <div style={{
                width: '1px',
                backgroundColor: '#52575C',
                alignSelf: 'stretch',
                margin: '2px 8px'
              }} />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '4px 8px',
              }}>
                <span style={{ fontWeight: 400 }}>{formatDate(data.createdAt)}</span>
              </div>
            </>
          )}

          <div style={{
            width: '1px',
            backgroundColor: '#52575C',
            alignSelf: 'stretch',
            margin: '2px 8px'
          }} />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
          }}>
            <span style={{ fontWeight: 400 }}>Status:</span>
            <span style={{
              marginLeft: '4px',
              fontWeight: 700,
              color: getStatusColor(data.isEnabled || data.isEnable)
            }}>
              {(data.isEnabled || data.isEnable) ? 'Aktif' : 'Tidak Aktif'}
            </span>
          </div>

          <div style={{
            width: '1px',
            backgroundColor: '#52575C',
            alignSelf: 'stretch',
            margin: '2px 8px'
          }} />

          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
          }}>
            <span style={{ fontWeight: 400 }}>Jenis Persetujuan:</span>
            <span style={{
              marginLeft: '4px',
              fontWeight: 700,
              color: getRequiredColor(data.isRequired)
            }}>
              {data.isRequired ? 'Wajib' : 'Tidak Wajib'}
            </span>
          </div>
        </div>

        <div className={styles.contentContainer}>
          <div className={styles.titleContainer}>
            <div className={styles.previewTitle}>{data.title}</div>
          </div>
          {parse(dompurify.sanitize(data.content || ''))}
          <div className={styles['form-action']}>
            <Group grow position="apart">
              <Button
                onClick={handleBack}
                sx={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                variant="outline"
              >
                Kembali
              </Button>
              <Button
                onClick={handleUpdate}
                sx={{ backgroundColor: 'var(--primary-color)' }}
                variant="filled"
              >
                Perbarui
              </Button>
            </Group>
          </div>
        </div>
      </Card>
    </div>
  );
}
